import { yupResolver } from "@hookform/resolvers/yup";
import { useCallback, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import * as yup from "yup";

import ActionIconButton from "@components/buttons/ActionIconButton";
import TertiaryButton from "@components/buttons/TertiaryButton";
import DateAndTimeStamp from "@components/dates/DateAndTimeStamp";
import DeleteConfirmationAlert from "@components/DeleteConfirmationAlert";
import BaseErrors from "@components/fields/BaseErrors";
import Input from "@components/fields/Input";
import RichTextArea from "@components/fields/RichTextArea";
import InternalNote from "@components/forms/careProfile/InternalNote";
import styles from "@components/forms/careProfile/InternalNotes.module.css";
import Form from "@components/forms/Form";
import FormCondition from "@components/forms/FormCondition";
import InternalNoteForm from "@components/forms/InternalNoteForm";
import Modal from "@components/Modal";
import BodyCopy from "@components/typography/BodyCopy";
import useCreateInternalNote from "@hooks/mutations/useCreateInternalNote";
import useDestroyInternalNote from "@hooks/mutations/useDestroyInternalNote";
import useFormPersist from "@hooks/useFormPersist";
import useOpener from "@hooks/useOpener";
import useSanitizeHtml from "@hooks/useSanitizeHtml";
import { GetJourneyWithCareProfileQuery, GetJourneyWithExpertPlanQuery, InternalNoteKindEnum } from "@typing/Generated";
import { handleMutationResponse } from "@utils/formUtils";
import { sortByTimestamp } from "@utils/modelUtils";

type FormData = {
  body: string;
  title?: string | null;
};

type Props = {
  journey: GetJourneyWithCareProfileQuery["journey"];
  noteKind: InternalNoteKindEnum;
};

type Note = NonNullable<GetJourneyWithExpertPlanQuery["journey"]["internalNotes"]>[0];

const InternalNotes = ({ journey, noteKind }: Props) => {
  const intl = useIntl();

  const [editorRefresh, setEditorRefresh] = useState(false);
  const [editingCurrentNote, setEditingCurrentNote] = useState(false);
  const [baseErrors, setBaseErrors] = useState<string[]>([]);
  const [noteBeingDeleted, setNoteBeingDeleted] = useState<Note | undefined>(undefined);
  const [noteBeingEdited, setNoteBeingEdited] = useState<Note | undefined>(undefined);

  const deleteOpener = useOpener();
  const editOpener = useOpener();
  const versionHistoryOpener = useOpener();

  const createInternalNote = useCreateInternalNote({ journeyId: journey.id });

  const destroyInternalNote = useDestroyInternalNote({ journeyId: journey.id });

  const handleDelete = useCallback(() => {
    if (noteBeingDeleted) {
      destroyInternalNote({ id: noteBeingDeleted.id });
    }
    setNoteBeingDeleted(undefined);
    deleteOpener.close();
  }, [deleteOpener, destroyInternalNote, noteBeingDeleted]);

  const internalNotes = sortByTimestamp(
    journey.internalNotes?.filter(note => note.kind === noteKind) ?? [],
    i => i.createdAt
  );

  const previousInternalNotes = useMemo(() => {
    if (noteKind === InternalNoteKindEnum.NOTE) {
      return internalNotes;
    }

    return internalNotes.slice(1);
  }, [internalNotes, noteKind]);

  const versionedNotes = [InternalNoteKindEnum.ROADMAP, InternalNoteKindEnum.SYNOPSIS].includes(noteKind);
  const currentVersionedNote = versionedNotes ? (internalNotes[0] as Note | undefined) : undefined;
  const sanitizedCurrentVersionedNoteBody = useSanitizeHtml(
    currentVersionedNote?.body,
    journey.id,
    currentVersionedNote ?? null
  );

  const defaultValues: FormData = {
    body: versionedNotes ? currentVersionedNote?.body ?? "" : "",
    title: null
  };

  const schema: yup.ObjectSchema<FormData> = yup
    .object()
    .shape({
      body: yup.string().required(intl.formatMessage({ id: "errors.internalNote.body.blank" })),
      title: yup.string().nullable()
    })
    .required();

  const form = useForm<FormData>({
    defaultValues,
    mode: "onChange",
    resolver: yupResolver(schema)
  });

  const formPersist = useFormPersist("internalNoteForm", {
    form,
    onDataRestored: () => {
      setEditorRefresh(!editorRefresh);
    },
    uniqueId: `${journey.id}-${noteKind}`
  });

  const onSubmit = useCallback(async () => {
    setBaseErrors([]);

    const values = {
      body: form.getValues().body,
      journeyId: journey.id,
      kind: noteKind,
      title: form.getValues().title
    };

    return createInternalNote(values).then(response => {
      handleMutationResponse<FormData>({
        data: response?.internalNoteCreate,
        intl,
        onSuccess: () => {
          setEditingCurrentNote(false);
          formPersist.clear();
          if (!versionedNotes) {
            form.reset();
          }
        },
        setBaseErrors,
        setError: form.setError,
        txBase: "errors.internalNote"
      });
    });
  }, [form, journey.id, noteKind, createInternalNote, intl, formPersist, versionedNotes]);

  return (
    <>
      {!editingCurrentNote && versionedNotes && (
        <div className={styles.currentVersionBox}>
          <button
            className={styles.spreadRow}
            onClick={() => {
              setEditingCurrentNote(true);
            }}
          >
            <BodyCopy bold>
              <FormattedMessage id={`models.internalNote.kinds.${noteKind}`} />
            </BodyCopy>
            <ActionIconButton ariaLabelKey="accessibility.ariaLabels.edit" icon="pencil" />
          </button>
          {currentVersionedNote && (
            <div>
              <div className="ion-margin-bottom">
                <BodyCopy color="gray-300" size="small">
                  <DateAndTimeStamp value={currentVersionedNote.createdAt} />
                </BodyCopy>
              </div>
              {currentVersionedNote?.title && <BodyCopy bold>{currentVersionedNote.title}</BodyCopy>}
              {currentVersionedNote?.body && <>{sanitizedCurrentVersionedNoteBody}</>}
            </div>
          )}
        </div>
      )}
      {(editingCurrentNote || !versionedNotes) && (
        <Form hookForm={form} onSubmit={form.handleSubmit(onSubmit)}>
          <BaseErrors errors={baseErrors} />
          <div className={styles.relativeContainer}>
            <FormCondition condition={!versionedNotes} fieldName="title" form={form}>
              <Input<FormData>
                error={form.formState.errors.title}
                noSpacing
                placeholder={intl.formatMessage({ id: "pages.carePlan.base.expert.title" })}
                register={form.register("title")}
              />
            </FormCondition>
            <RichTextArea<FormData>
              contentRefreshSwitch={editorRefresh}
              control={form.control}
              error={form.formState.errors.body}
              extraStyles={versionedNotes ? "" : styles.editorWithTitle}
              journeyId={journey.id}
              name="body"
              placeholder={intl.formatMessage({ id: "pages.carePlan.base.expert.typeNotesHere" })}
              sharableURLSHidden
              size="small"
            />
            <div className={form.formState.errors.body ? styles.submitIconsWithError : styles.submitIcons}>
              <ActionIconButton
                ariaLabelKey="accessibility.ariaLabels.add"
                color="success"
                disabled={!form?.formState.isValid || form?.formState.isSubmitting}
                icon="check"
                onClick={form.handleSubmit(onSubmit)}
              />
              {versionedNotes && (
                <ActionIconButton
                  ariaLabelKey="accessibility.ariaLabels.delete"
                  color="danger-300"
                  icon="times"
                  onClick={() => {
                    setEditingCurrentNote(false);
                    form.reset();
                  }}
                />
              )}
            </div>
          </div>
        </Form>
      )}
      {versionedNotes && previousInternalNotes.length > 0 && (
        <TertiaryButton
          icon={versionHistoryOpener.isOpen ? "chevronUp" : "chevronDown"}
          iconPosition="end"
          onClick={versionHistoryOpener.toggle}
          textKey={
            versionHistoryOpener.isOpen
              ? "models.internalNote.hideVersionHistory"
              : "models.internalNote.showVersionHistory"
          }
        />
      )}
      {(!versionedNotes || versionHistoryOpener.isOpen) && (
        <div className={styles.internalNotes}>
          {previousInternalNotes.map(note => (
            <InternalNote
              handleDelete={() => {
                setNoteBeingDeleted(note);
                deleteOpener.open();
              }}
              handleEdit={() => {
                setNoteBeingEdited(note);
                editOpener.open();
              }}
              journeyId={journey.id}
              key={note.id}
              note={note}
            />
          ))}
        </div>
      )}
      <DeleteConfirmationAlert
        messageKey="models.internalNote.reallyDelete"
        onDelete={handleDelete}
        opener={deleteOpener}
      />
      <Modal opener={editOpener} title={intl.formatMessage({ id: "models.internalNote.editInternalNote" })}>
        {() => (
          <InternalNoteForm
            internalNote={noteBeingEdited}
            journeyId={journey.id}
            onCancel={editOpener.close}
            onSuccess={editOpener.close}
          />
        )}
      </Modal>
    </>
  );
};

export default InternalNotes;
